// @givmo/compliance — "Use AI on your organization’s data" (CCM-R1-AI-SETTING; D-CCM-21 set C Q2,
// Q3, A-C3): the organization-level setting that governs GIVMO's AI on the organization's data.
//
// ══ THE WIRE IS GENERATED; THE READ IS STILL DECODED ═══════════════════════════════════════════
//
// The snapshot refresh off backend `f134e3a05` (CCM-R1-INTAKE (a)) generated both routes, so the
// write body is the generated `ComplianceAiSettingWrite` and a host calls the generated operations.
// The read keeps its tolerant decode (`agent_docs/api-client.md` → graduating an escape hatch: "the
// tolerant decode stays"): a generated type describes the wire, not what arrived.
//
// ══ ONLY A RECORDED BOOLEAN SAYS WHETHER IT IS ON ══════════════════════════════════════════════
//
// The decode never guesses. A body without a real `givmo_ai_enabled` boolean or a whole,
// non-negative `revision` is no answer at all (`null`), and the card then shows no state rather
// than a default: a switch drawn ON from a malformed body would tell an organization that turned
// Givmo's AI off that it is on.

import { firstErrorCode, isGivmoError } from '@givmo/api-client';
import { SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE } from './compliance-signing-model';
import { throughTheirAi } from './review-words';

/** The organization's AI setting, as the server answers it. */
export interface ComplianceAiSetting {
  /** Whether Givmo's AI may act on the organization's data. On until the organization turns it off. */
  givmoAiEnabled: boolean;
  /** The recorded revision the value comes from: 0 when it was never changed. */
  revision: number;
  /** When it last changed, or `null` at revision 0. */
  changedAt: string | null;
  /** Who last changed it, or `null` at revision 0. */
  changedByUserId: string | null;
  /**
   * CCM-R1-AI-SETTING-VIA-AI: how it last changed, `web` (a person in Givmo) or `connector`
   * (through their connected AI); `null` at revision 0 and from a backend that doesn't say.
   */
  channel: string | null;
}

/** The change: the new value, and the revision it was made from (the server refuses a stale one). */
export type { ComplianceAiSettingWrite } from '@givmo/api-client';

/** The `error_code` the backend puts on a change made from a stale view, which records nothing. */
const AI_SETTING_STALE_ERROR_CODE = 'COMPLIANCE_AI_SETTING_STALE';

/**
 * Whether a change was refused because it was made from a stale view: the setting changed after it
 * was read. Keyed on the envelope's `error_code`, never on the line's text.
 */
export function isAiSettingStale(error: unknown): boolean {
  return (
    isGivmoError(error) &&
    error.status === 409 &&
    firstErrorCode(error) === AI_SETTING_STALE_ERROR_CODE
  );
}

/** The read's query key, spelled once: the card writes the change's answer straight into it. */
export function complianceAiSettingQueryKey(orgRef: string | null) {
  return ['compliance', 'ai-setting', orgRef] as const;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** The setting out of a response body, or `null` when the body says nothing a person can trust. */
export function decodeComplianceAiSetting(value: unknown): ComplianceAiSetting | null {
  if (!isRecord(value)) return null;
  const enabled = value.givmo_ai_enabled;
  const revision = value.revision;
  if (typeof enabled !== 'boolean') return null;
  if (typeof revision !== 'number' || !Number.isInteger(revision) || revision < 0) return null;
  return {
    givmoAiEnabled: enabled,
    revision,
    changedAt: typeof value.changed_at === 'string' ? value.changed_at : null,
    changedByUserId: typeof value.changed_by_user_id === 'string' ? value.changed_by_user_id : null,
    channel: typeof value.channel === 'string' ? value.channel : null,
  };
}

/**
 * The card's last change (D-CCM-96, frames A1a and A1b): who changed the setting, how and when.
 * Only "off" can come through a person's connected AI; turning it back on is a person's act alone
 * (D-CCM-54). A setting never changed (revision 0) shows no line. `name` is the person the host's
 * roster names, if it does; `day` is the change's day as the viewer reads it.
 */
export function aiSettingChangeLine(
  setting: ComplianceAiSetting,
  name: string | undefined,
  day: string,
): string | undefined {
  if (setting.revision === 0) return undefined;
  if (!setting.givmoAiEnabled && setting.channel === 'connector') {
    return `Turned off through ${throughTheirAi(name)} on ${day}.`;
  }
  // Mid-sentence, after "by": lowercase where the roster can't name them (D-CCM-99).
  const who = name ?? SOMEONE_IN_YOUR_ORGANIZATION_MID_SENTENCE;
  return setting.givmoAiEnabled
    ? `Turned on by ${who} on ${day}.`
    : `Turned off by ${who} on ${day}.`;
}
